import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { Layout } from './components/layout/Layout';
import { RecordList } from './components/records/RecordList';
import {
  BILLS, CHARGE_LINES, COMMERCIAL, CUSTOMERS, ENGAGEMENTS, EXPENSES, INVOICE_TEMPLATES, INVOICES, PAYMENTS_IN, PAYMENTS_OUT, PEOPLE, PURCHASE_ORDERS, REPORTING_CUSTOMERS, SUPPLIER_PURCHASE_ORDERS, VENDORS,
} from './lib/record-schemas';
import { LoginPage } from './pages/Login';
import { HomePage } from './pages/Home';
import { ReviewPage } from './pages/Review';
import { InvoicePage } from './pages/Invoice';
import { InvoiceTemplatePage } from './pages/InvoiceTemplate';
import { QuotationPage } from './pages/Quotation';
import { ProposalPage } from './pages/Proposal';
import { PurchaseOrderPage } from './pages/PurchaseOrder';
import { BillPage } from './pages/Bill';
import { InboxPage } from './pages/Inbox';
import { ReportLayouts } from './pages/reports/ReportLayouts';
import { BudgetsPage } from './pages/Budgets';
import { JobsPage } from './pages/Jobs';
import { ProcessesPage } from './pages/Processes';
import { AgentsPage } from './pages/Agents';
import { SettingsPage } from './pages/Settings';
import { ChatRedirect } from './pages/ChatRedirect';
import { ReviewIssuePage } from './pages/ReviewIssue';
import { InvoiceReviewPage } from './pages/InvoiceReview';
import { CreditNoteReviewPage } from './pages/CreditNoteReview';
import { PurchaseOrderReviewPage } from './pages/PurchaseOrderReview';
import { OfferCopyPage } from './pages/OfferCopy';
import { SalesOfferPage } from './pages/SalesOffer';

function Gate({ children }: { children: ReactNode }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="flex min-h-screen items-center justify-center">Loading</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function LoginGate() {
  const { user, ready } = useAuth();
  if (!ready) return <div className="flex min-h-screen items-center justify-center">Loading</div>;
  if (user) return <Navigate to="/" replace />;
  return <LoginPage />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginGate />} />
      <Route path="/" element={<Gate><HomePage /></Gate>} />
      <Route path="/review" element={<Gate><ReviewPage /></Gate>} />
      <Route path="/customers" element={<Gate><RecordList schema={CUSTOMERS} /></Gate>} />
      <Route path="/reporting-customers" element={<Gate><RecordList schema={REPORTING_CUSTOMERS} /></Gate>} />
      <Route path="/engagements" element={<Gate><RecordList schema={ENGAGEMENTS} /></Gate>} />
      <Route path="/charge-lines" element={<Gate><RecordList schema={CHARGE_LINES} /></Gate>} />
      <Route path="/purchase-orders" element={<Gate><RecordList schema={PURCHASE_ORDERS} /></Gate>}>
        <Route path="new" element={<PurchaseOrderPage />} />
        <Route path=":id" element={<PurchaseOrderPage />} />
      </Route>
      <Route path="/invoice-templates" element={<Gate><RecordList schema={INVOICE_TEMPLATES} /></Gate>}>
        <Route path="new" element={<InvoiceTemplatePage />} />
        <Route path=":id" element={<InvoiceTemplatePage />} />
      </Route>
      <Route path="/invoices" element={<Gate><RecordList schema={INVOICES} /></Gate>}>
        <Route path="new" element={<InvoicePage />} />
        <Route path=":id" element={<InvoicePage />} />
      </Route>
      <Route path="/quotations" element={<Gate><RecordList schema={COMMERCIAL} /></Gate>}>
        <Route path="new" element={<QuotationPage />} />
        <Route path=":id" element={<SalesOfferPage side="quotation" />} />
      </Route>
      <Route path="/sales-offers/new" element={<Gate><OfferCopyPage /></Gate>} />
      <Route path="/sales-offers/:id/review" element={<Gate><ReviewIssuePage /></Gate>} />
      <Route path="/invoices/:id/review" element={<Gate><InvoiceReviewPage /></Gate>} />
      <Route path="/invoices/:id/credit-notes/:noteId/review" element={<Gate><CreditNoteReviewPage /></Gate>} />
      <Route path="/purchase-orders/:id/review" element={<Gate><PurchaseOrderReviewPage /></Gate>} />
      <Route path="/supplier-purchase-orders/:id/review" element={<Gate><PurchaseOrderReviewPage /></Gate>} />
      <Route path="/proposals/new" element={<Gate><ProposalPage /></Gate>} />
      <Route path="/proposals/:id" element={<Gate><SalesOfferPage side="proposal" /></Gate>} />
      <Route path="/payments-in" element={<Gate><RecordList schema={PAYMENTS_IN} /></Gate>} />
      <Route path="/vendors" element={<Gate><RecordList schema={VENDORS} /></Gate>} />
      <Route path="/supplier-purchase-orders" element={<Gate><RecordList schema={SUPPLIER_PURCHASE_ORDERS} /></Gate>}>
        <Route path="new" element={<PurchaseOrderPage />} />
        <Route path=":id" element={<PurchaseOrderPage />} />
      </Route>
      <Route path="/bills" element={<Gate><RecordList schema={BILLS} /></Gate>}>
        <Route path=":id" element={<BillPage />} />
      </Route>
      <Route path="/expenses" element={<Gate><RecordList schema={EXPENSES} /></Gate>} />
      <Route path="/people" element={<Gate><RecordList schema={PEOPLE} /></Gate>} />
      <Route path="/payments-out" element={<Gate><RecordList schema={PAYMENTS_OUT} /></Gate>} />
      <Route path="/inbox" element={<Gate><InboxPage /></Gate>} />
      <Route path="/jobs" element={<Gate><JobsPage /></Gate>} />
      <Route path="/processes" element={<Gate><ProcessesPage /></Gate>} />
      <Route path="/budgets" element={<Gate><BudgetsPage /></Gate>} />
      <Route path="/reports" element={<Navigate to="/?report=receivable" replace />} />
      <Route path="/reports/resourcing" element={<Navigate to="/?report=resourcing" replace />} />
      <Route path="/reports/layouts" element={<Gate><ReportLayouts /></Gate>} />
      <Route path="/chat" element={<Gate><ChatRedirect /></Gate>} />
      <Route path="/agents" element={<Gate><AgentsPage /></Gate>} />
      <Route path="/settings" element={<Gate><SettingsPage /></Gate>} />
    </Routes>
  );
}
